import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Anchor, Badge, Box, Button, Card, Flex, Grid, Switch, Text, Tooltip } from '@mantine/core';
import { IconChartLine, IconClock, IconGripVertical, IconHistory, IconSignLeft } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import HeaderStatistic from '../../../components/HeaderStatistic';
import LazyLoadSpinner from '../../../components/LazyLoaderSpinner';
import Metadata from '../../../components/Metadata';
import PriorityOrderBar from '../../../components/PriorityOrderBar';
import Spinner from '../../../components/Spinner';
import { Page } from '../../../constants/pageTitles';
import useFetch from '../../../helpers/useFetch';

const SortableItem = ({ bannerBlock, linkToBanner, isExpired, toggleBannerBlock, statsElements, priority }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: bannerBlock.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const location = useLocation();

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card mb="md" data-tour={`bannerBlocksName=${bannerBlock.name}`} withBorder>
        <Card.Section inheritPadding py="sm">
          <Flex gap="md" align="flex-start">
            <Tooltip label={`Priority ${priority}`} position="left" withArrow>
              <Box {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center', gap: 4, paddingTop: 4 }}>
                <Text size="sm" fw={700} c="dimmed" style={{ minWidth: 20, textAlign: 'center' }}>{priority}</Text>
                <IconGripVertical size={20} color="var(--mantine-color-dimmed)" />
              </Box>
            </Tooltip>
            <Box style={{ flex: 1 }}>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <Anchor
                    href={`#${location.pathname}/${bannerBlock.pageId}/bannerBlocks/${bannerBlock.id}/details`}
                    fw={600}
                    mb="xs"
                    style={{ display: 'block' }}
                    data-tour="bannerBlockNameLink"
                  >
                    {bannerBlock.name}
                  </Anchor>
                  <Flex gap="xs" wrap="wrap" align="center">
                    <Badge variant="default">
                      <IconSignLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      <Anchor href={linkToBanner} target="_blank" size="xs">
                        {bannerBlock.banner?.name || 'Unknown'}
                        {(bannerBlock.banner?.startTime || bannerBlock.banner?.endTime) && (
                          <>
                            {' '}
                            (
                            {bannerBlock.banner.startTime
                              ? new Date(bannerBlock.banner.startTime).toLocaleString(Navigator.language)
                              : ''}{' '}
                            -{' '}
                            {bannerBlock.banner.endTime
                              ? new Date(bannerBlock.banner.endTime).toLocaleString(Navigator.language)
                              : ''}
                            )
                          </>
                        )}
                      </Anchor>
                    </Badge>
                    <Tooltip
                      label={
                        <>
                          <div>Created: {new Date(bannerBlock.createdAt).toLocaleString()}</div>
                          <div>Updated: {new Date(bannerBlock.updatedAt).toLocaleString()}</div>
                        </>
                      }
                      position="top"
                      withArrow
                    >
                      <Badge variant="default" style={{ cursor: 'pointer' }}>
                        <IconHistory size={14} style={{ verticalAlign: 'middle' }} />
                      </Badge>
                    </Tooltip>
                    {bannerBlock.sessionInterval && (
                      <Badge variant="default">
                        <IconClock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Every {bannerBlock.sessionInterval} {bannerBlock.sessionInterval === 1 ? 'session' : 'sessions'}
                      </Badge>
                    )}
                  </Flex>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Flex justify="flex-end" align="center" gap="sm" h="100%">
                    <Button
                      component="a"
                      href={`#${location.pathname}/${bannerBlock.pageId}/bannerBlocks/${bannerBlock.id}/analytics`}
                      size="xs"
                    >
                      Statistics <IconChartLine size={14} style={{ marginLeft: 4 }} />
                    </Button>
                    <Box data-tour="bannerBlockRunBtn">
                      {isExpired ? (
                        <Text fw={700} c="red">
                          EXPIRED
                        </Text>
                      ) : (
                        <Switch size="lg" checked={bannerBlock.running} onChange={toggleBannerBlock} />
                      )}
                    </Box>
                  </Flex>
                </Grid.Col>
              </Grid>
            </Box>
          </Flex>
        </Card.Section>
        <Card.Section inheritPadding py="sm">
          <Grid>
            <Grid.Col span={12}>
              <Grid>{statsElements}</Grid>
            </Grid.Col>
          </Grid>
        </Card.Section>
      </Card>
    </div>
  );
};

const BannerBlocksPage = ({ page, updatePage, stats }) => {
  const { accountId, domainId } = useParams();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const currentDomain = useSelector((state) => state.domain.domain);
  const domainCurrency = currentDomain.currency;

  const fetch = useFetch();

  const toggleBannerBlock = (bannerBlockIndex, running) => {
    const bannerBlock = page.bannerBlocks[bannerBlockIndex];
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/pages/${bannerBlock.pageId}/bannerBlocks/${bannerBlock.id}`,
      {
        method: 'PUT',
        body: JSON.stringify({ bannerBlock: { running } }),
      }
    )
      .then((result) => {
        const modifiedPage = { ...page };
        page.bannerBlocks[bannerBlockIndex] = result.bannerBlock;
        updatePage(modifiedPage);
      })
      .catch(console.error);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id === over.id) {
      return;
    }

    const oldIndex = page.bannerBlocks.map((b) => b.id.toString()).indexOf(active.id);
    const newIndex = page.bannerBlocks.map((b) => b.id.toString()).indexOf(over.id);
    const bannerBlocks = [...page.bannerBlocks];

    const rearrangedBannerBlocks = arrayMove(bannerBlocks, oldIndex, newIndex);
    updatePage(Object.assign({}, page, { bannerBlocks: rearrangedBannerBlocks }));

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pages/${page.id}/bannerBlocks/rearrange`, {
      method: 'POST',
      body: JSON.stringify({ bannerBlocks: rearrangedBannerBlocks }),
    })
      .then(() => {})
      .catch(() => {
        updatePage(Object.assign({}, page, { bannerBlocks }));
      });
  };

  if (!page) {
    return <Spinner />;
  }

  let bannerBlockElements = [];
  for (let j = 0; j < page.bannerBlocks.length; j++) {
    const bannerBlock = page.bannerBlocks[j];
    const isExpired =
      bannerBlock.banner.endTime && new Date(bannerBlock.banner.endTime).getTime() < new Date().getTime();

    let statsElements;

    if (!stats) {
      statsElements = (
        <Grid.Col span={12}>
          <Grid justify="center">
            <LazyLoadSpinner />
          </Grid>
        </Grid.Col>
      );
    } else {
      statsElements = (
        <>
          <Grid.Col span="auto" style={{ textAlign: 'center' }}>
            <HeaderStatistic title="Page Views" stats={stats} stat="views" id={page.token} statKey="count" />
          </Grid.Col>
          <Grid.Col span="auto" style={{ textAlign: 'center' }}>
            <HeaderStatistic
              title="Impressions"
              stats={stats}
              stat="impressions"
              id={bannerBlock.token}
              percentageOfStat="views"
              percentageOfId={page.token}
              statKey="count"
            />
          </Grid.Col>
          <Grid.Col span="auto" style={{ textAlign: 'center' }}>
            <HeaderStatistic
              title="Closes"
              stats={stats}
              stat="closes"
              id={bannerBlock.token}
              percentageOfStat="impressions"
              statKey="count"
            />
          </Grid.Col>
          <Grid.Col span="auto" style={{ textAlign: 'center' }}>
            <HeaderStatistic
              title="Clicks"
              stats={stats}
              stat="clicks"
              id={bannerBlock.token}
              percentageOfStat="impressions"
              statKey="count"
            />
          </Grid.Col>
          <Grid.Col span="auto" style={{ textAlign: 'center' }}>
            <HeaderStatistic
              title="Conversions"
              stats={stats}
              stat="conversions"
              id={bannerBlock.token}
              percentageOfStat="clicks"
              statKey="count"
            />
          </Grid.Col>
          <Grid.Col span="auto" style={{ textAlign: 'center' }}>
            <HeaderStatistic
              title="Purchases"
              stats={stats}
              stat="purchases"
              percentageOfStat="conversions"
              id={bannerBlock.token}
              statKey="count"
            />
          </Grid.Col>
          <Grid.Col span="auto" style={{ textAlign: 'center' }}>
            <HeaderStatistic
              title="Revenue"
              stats={stats}
              stat="purchases"
              id={bannerBlock.token}
              statKey="value"
              currency={domainCurrency}
            />
          </Grid.Col>
        </>
      );
    }
    const linkToBanner = bannerBlock.banner
      ? `#/accounts/${accountId}/domains/${domainId}/banners/${bannerBlock.banner.id}/details`
      : null;

    bannerBlockElements.push(
      <SortableItem
        key={`bannerBlock-${bannerBlock.id}`}
        index={j}
        priority={j + 1}
        bannerBlock={bannerBlock}
        linkToBanner={linkToBanner}
        isExpired={isExpired}
        toggleBannerBlock={(e) => toggleBannerBlock(j, e.target.checked)}
        statsElements={statsElements}
      />
    );
  }

  return (
    <>
      <Metadata title={Page.bannerBlocks} />
      {page.bannerBlocks.length > 1 && <PriorityOrderBar />}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={page.bannerBlocks.map((b) => b.id.toString())}
          strategy={verticalListSortingStrategy}
        >
          <div data-tour="bannerBlocksCards">{bannerBlockElements}</div>
        </SortableContext>
      </DndContext>
    </>
  );
};

export default BannerBlocksPage;
